// src/pages/Product/ImplantLabel.js
// Dental implant label (Tyvek label) - 65mm x 35mm, drawn as one SVG in real millimetres.
// Shape: top-left corner cut (12mm x 11mm), bottom-left corner rounded (4mm),
// left panel 35mm wide, right panel 30mm wide with 3 equal blocks (REF / LOT / name).
//
// PRINT QUALITY NOTES (thermal printers)
// - Text is now bigger. The name block picks the LARGEST font size that fits its
//   space (it breaks into more lines instead of squeezing the letters).
// - Text gets a hair-thin outline so the strokes stay solid when printed.
// - The size line is no longer printed: the variation name already holds it.

import React from "react";

/* ================= WHEN IS THIS LABEL USED? =================
   Only products whose CATEGORY name contains "Dental Implant"
   (or whose category.group === 'dental implant') get this label.
   "Implant Level Ti-Base", "Implant Level Scanbody" etc. are NOT
   dental implants — they keep the normal 60mm × 40mm label.
   To change the rule, change only this function. */
export const isImplantProduct = (product) => {
  if (!product) return false;

  const category = product.product_category || {};
  const categoryName = String(category.category_Name || "").toLowerCase().trim();
  const group = String(category.group || "").toLowerCase().trim();

  return group === "dental implant" || categoryName.includes("dental implant");
};

/* ================= TEXT SHOWN ON THE LABEL ================= */
export const IFU_TEXT = "ifu.open source.com";

export const LABEL_WIDTH_MM = 65;
export const LABEL_HEIGHT_MM = 35;

/* ================= DATE HELPERS (YYYY-MM-DD) ================= */
const pad = (value) => String(value).padStart(2, "0");

export const todayISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const addYears = (isoDate, years) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || "");
  if (!match) return "";
  return `${Number(match[1]) + years}-${match[2]}-${match[3]}`;
};

/* Kept for other files that import it (not used on this label any more) */
export const buildImplantSpec = (product) =>
  [product?.size, product?.product_variation?.variation_name]
    .filter(Boolean)
    .join(", ");

/* ================= SMALL HELPERS ================= */
const FONT = "Arial, Helvetica, sans-serif";
const BLACK = "#000000";

// Hair-thin outline: makes thin strokes print darker / more solid on thermal printers.
const THICKEN = {
  stroke: BLACK,
  strokeWidth: 0.05,
  strokeLinejoin: "round",
  paintOrder: "stroke",
};

// Last-resort squeeze, only for a single line that is still too long.
const fit = (text, fontSize, maxLength, avgEm = 0.56) => {
  const natural = String(text || "").length * avgEm * fontSize;
  return natural > maxLength
    ? { textLength: maxLength, lengthAdjust: "spacingAndGlyphs" }
    : {};
};

// Greedy word wrap for `maxChars` characters per line.
const greedyWrap = (words, maxChars) => {
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (!current || next.length <= maxChars) {
      current = next;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  return lines;
};

// Breaks a text into neat, BALANCED lines at word boundaries.
// "One Connection Bone Level" -> ["One Connection", "Bone Level"]
export const wrapLines = (text, fontSize, maxWidth, avgEm = 0.58, maxLines = 2) => {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const maxChars = Math.max(1, Math.floor(maxWidth / (fontSize * avgEm)));
  let lines = greedyWrap(words, maxChars);

  const count = lines.length;
  if (count > 1 && count <= maxLines) {
    let limit = maxChars;
    while (limit > 1) {
      const trial = greedyWrap(words, limit - 1);
      if (trial.length !== count) break;
      limit -= 1;
      lines = trial;
    }
  }

  if (lines.length > maxLines) {
    const head = lines.slice(0, maxLines - 1);
    head.push(lines.slice(maxLines - 1).join(" "));
    return head;
  }
  return lines;
};

/* ================= AUTO-FIT NAME BLOCK =================
   The variation name holds everything, e.g.
     "One Connection Bone Level , Ø 4.0mm / L10mm SLA"
   It is split at the comma, so every part starts on its own line:
     One Connection Bone Level
     Ø 4.0mm / L10mm SLA
   Then the LARGEST font size (maxFont -> minFont) is chosen for which all lines
   fit the width and the lines fit the height. A part that is too wide for one
   line is wrapped onto 2 lines instead of being squeezed.
   The LAST line always sits on `lastBaseline`; the block grows upwards. */
const nameParts = (name) =>
  String(name || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const layoutName = (
  name,
  { maxWidth, firstBaseline, lastBaseline, maxFont, minFont, lineGap = 1.12, avgEm = 0.58 }
) => {
  const parts = nameParts(name);
  if (parts.length === 0) return { lines: [], font: minFont, step: 0 };

  const span = lastBaseline - firstBaseline;
  const breakInto = (font) =>
    parts.flatMap((part) => wrapLines(part, font, maxWidth, avgEm, 2));

  for (let font = maxFont; font >= minFont - 0.001; font = Math.round((font - 0.05) * 100) / 100) {
    const lines = breakInto(font);
    const step = font * lineGap;
    const heightOk = (lines.length - 1) * step <= span + 0.001;
    const widthOk = lines.every((line) => line.length * font * avgEm <= maxWidth + 0.01);
    if (heightOk && widthOk) return { lines, font, step };
  }

  // nothing fits: smallest font, limited number of lines, squeeze as a last resort
  const font = minFont;
  const step = font * lineGap;
  let lines = breakInto(font);
  const maxLines = Math.max(1, Math.floor(span / step) + 1);
  if (lines.length > maxLines) {
    const head = lines.slice(0, maxLines - 1);
    head.push(lines.slice(maxLines - 1).join(" "));
    lines = head;
  }
  return { lines, font, step };
};

const NameBlock = ({ x, name, ...options }) => {
  const { lines, font, step } = layoutName(name, options);
  if (lines.length === 0) return null;

  const startY = options.lastBaseline - step * (lines.length - 1);

  return (
    <g>
      {lines.map((line, i) => (
        <text
          key={i}
          x={x}
          y={startY + i * step}
          fontFamily={FONT}
          fontWeight="700"
          fontSize={font}
          fill={BLACK}
          {...THICKEN}
          {...fit(line, font, options.maxWidth, options.avgEm || 0.58)}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

/* ================= IMAGE ICONS =================
   Each icon is an <image> placed inside the SVG using x/y/w/h (mm).
   Images are transparent PNGs (black icons on transparent bg). */
const ICON_BASE = "/implant-icons/";

const SvgImage = ({ href, x, y, w, h }) => (
  <image
    href={`${ICON_BASE}${href}`}
    x={x}
    y={y}
    width={w}
    height={h}
    preserveAspectRatio="xMidYMid meet"
  />
);

/* ================= RIGHT PANEL: ONE BLOCK (REF / LOT / name) ================= */
const BLOCK_HEIGHT = LABEL_HEIGHT_MM / 3;

const RightBlock = ({ top, data }) => (
  <g transform={`translate(0 ${top})`}>
    {/* REF */}
    <rect x="37.7" y="1.2" width="3.85" height="2.4" fill="none" stroke={BLACK} strokeWidth="0.2" />
    <text x="39.62" y="3.05" textAnchor="middle" fontFamily={FONT} fontWeight="900" fontSize="1.75" fill={BLACK}>
      REF
    </text>
    <text x="43" y="3.15" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK} {...THICKEN} {...fit(data.ref, 2.1, 21, 0.58)}>
      {data.ref}
    </text>

    {/* LOT */}
    <rect x="37.7" y="4.3" width="3.6" height="2.4" fill="none" stroke={BLACK} strokeWidth="0.2" />
    <text x="39.5" y="6.15" textAnchor="middle" fontFamily={FONT} fontWeight="900" fontSize="1.75" fill={BLACK}>
      LOT
    </text>
    <text x="42.9" y="6.25" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK} {...THICKEN} {...fit(data.lot, 2.1, 21, 0.58)}>
      {data.lot}
    </text>

    {/* name (auto-fit, larger) */}
    <NameBlock
      x={37.05}
      name={data.name}
      maxWidth={27.4}
      firstBaseline={8.3}
      lastBaseline={11}
      maxFont={2.2}
      minFont={1.4}
    />
  </g>
);

/* ================= THE LABEL ================= */
/* data = { ref, lot, name, mfgDate, expDate }
   (data.spec is ignored: the size is no longer printed)
   preview = true draws a light grey label shape (screen only - it is removed when printing). */
export const ImplantLabel = ({ data, preview = false }) => (
  <svg
    id="implant-label"
    xmlns="http://www.w3.org/2000/svg"
    width={`${LABEL_WIDTH_MM}mm`}
    height={`${LABEL_HEIGHT_MM}mm`}
    viewBox={`0 0 ${LABEL_WIDTH_MM} ${LABEL_HEIGHT_MM}`}
    style={{ display: "block", background: "#ffffff", overflow: "visible" }}
  >
    {preview && (
      <g className="preview-only">
        <path
          d="M12 0 H65 V35 H4 A4 4 0 0 1 0 31 V11 Z"
          fill="#f1f1f1"
          stroke="#9ca3af"
          strokeWidth="0.15"
          strokeDasharray="0.8 0.6"
        />
      </g>
    )}

    {/* ===== dividers ===== */}
    <line x1="35" y1="0" x2="35" y2="35" stroke={BLACK} strokeWidth="0.2" />
    <line x1="35" y1={BLOCK_HEIGHT} x2="65" y2={BLOCK_HEIGHT} stroke={BLACK} strokeWidth="0.2" />
    <line x1="35" y1={BLOCK_HEIGHT * 2} x2="65" y2={BLOCK_HEIGHT * 2} stroke={BLACK} strokeWidth="0.2" />

    {/* ===== left panel: dates ===== */}
    <SvgImage href="01_expiry_hourglass.png" x={13.9} y={1.35} w={2.8} h={4.1} />
    <text x="18.1" y="4.25" fontFamily={FONT} fontWeight="700" fontSize="2.2" fill={BLACK} {...THICKEN}>
      {data.expDate}
    </text>

    <SvgImage href="02_manufacturing_factory.png" x={13.2} y={5.3} w={3.7} h={3.6} />
    <text x="18.1" y="8.5" fontFamily={FONT} fontWeight="700" fontSize="2.2" fill={BLACK} {...THICKEN}>
      {data.mfgDate}
    </text>

    {/* ===== left panel: symbols ===== */}
    <SvgImage href="03_keep_away_from_sunlight.png" x={5.3} y={13.2} w={4.3} h={4.5} />
    <SvgImage href="04_damaged_package.png" x={10.3} y={13} w={5} h={5} />
    <SvgImage href="05_single_use.png" x={16.1} y={13} w={5} h={5} />
    <SvgImage href="06_do_not_resterilize.png" x={22} y={13} w={5} h={5} />
    <SvgImage href="07_dashed_oval.png" x={27.6} y={13.2} w={6.9} h={4.2} />

    <text x="3.4" y="19.9" fontFamily={FONT} fontWeight="700" fontSize="1.9" fill={BLACK} {...THICKEN}>
      RX only
    </text>

    <SvgImage href="08_ifu_book.png" x={3.4} y={20.8} w={4} h={2.9} />
    <text
      x="7.9"
      y="23.3"
      fontFamily={FONT}
      fontWeight="700"
      fontSize="1.7"
      fill={BLACK}
      {...THICKEN}
      textLength="15.5"
      lengthAdjust="spacingAndGlyphs"
    >
      {IFU_TEXT}
    </text>

    <SvgImage href="09_sterile_R.png" x={24.6} y={20.7} w={8.8} h={3} />

    {/* ===== left panel: product name (auto-fit, larger) ===== */}
    <NameBlock
      x={5}
      name={data.name}
      maxWidth={29}
      firstBaseline={27.4}
      lastBaseline={32.6}
      maxFont={2.8}
      minFont={1.8}
    />

    {/* ===== right panel: 3 identical blocks ===== */}
    <RightBlock top={0} data={data} />
    <RightBlock top={BLOCK_HEIGHT} data={data} />
    <RightBlock top={BLOCK_HEIGHT * 2} data={data} />
  </svg>
);

export default ImplantLabel;